import ArrowLeftThinIcon from '@assets/icons/arrow-left-thin.svg';
import EmptyClockIcon from '@assets/icons/empty-clock.svg';
import EmptySheetIcon from '@assets/icons/empty-sheet.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ViewCircleIcon from '@assets/icons/view-circle.svg';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import {
  APP_MODAL_ROUTES,
  INSIGHTS_HUB_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import { createStyles } from '@/contexts/theme-context';
import { CurrentOrdersListSkeleton } from '@/components/insights-hub/current-orders-list-skeleton';
import {
  DEFAULT_MAX_PROGRESS_OFFSET,
  PullToRefreshLoader,
} from '@/components/pull-to-refresh-loader';
import {
  Dimensions,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  TouchableOpacity,
  View,
} from 'react-native';
import { EmptyState } from '@/components/empty-state';
import { fennelTrack } from '@/analytics/fennel/hook';
import { FilterPills } from '@/components/filter-pills';
import {
  FilterType,
  useCallSheetFiltering,
} from '@/hooks/call-sheet/use-call-sheet-filtering';
import { formatCurrency } from '@/utils/money';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { GESTURE_RESPONSE_DISTANCE } from '@/hooks/use-default-stack-screen-options';
import { GetHubCurrentOrdersResponse, OperatorEmployeeContact } from '@/types';
import { getTranslationValue } from '@/utils/translations';
import {
  HubOrder,
  HubOrderOption,
  isCurrentOrder,
  isPastOrder,
} from '@/hooks/insights-hub/order-option';
import {
  InsightsHubStackParams,
  InvoiceDetails,
  OrderDetails,
} from '@/navigators/types';
import { LIST_ITEM_MARGIN } from '@/components/history-list-item';
import { ListItemSeparator } from '@/components/list-item';
import { ListItemSkeleton } from '@usepepper/ui/molecules/skeletons/list-item-skeleton';
import { ListRenderItem } from '@shopify/flash-list';
import { ListSectionHeader, RecyclerList } from '@/components/recycler-list';
import { OrderAction } from '@/components/insights-hub/order-action';
import { PageContainer } from '@/components/page-container';
import { partnerSupplier } from '@/utils/environment';
import { PermissionAction, PermissionSubject } from '@/enums';
import { PhonePickerBottomSheet } from '@/components/insights-hub/phone-picker-bottom-sheet';
import { PillButton, PillButtonType } from '@/components/pill-button';
import { RestaurantInfoHeader } from '@/components/insights-hub/restaurant-info-header';
import { shadow } from '@/styles';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { TabView, Props as TabViewProps } from '@/components/tab-view';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCurrentOrders } from '@/hooks/insights-hub/use-current-orders';
import { useNavigation } from '@react-navigation/native';
import { usePastOrders } from '@/hooks/insights-hub/use-past-orders';
import { usePullToRefreshState } from '@/components/use-pull-to-refresh-state';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';

enum CurrentOrderTabRoutes {
  Current = 'Current',
  Past = 'Past',
}

export interface TabRoute {
  key: CurrentOrderTabRoutes;
  title: string;
}

type Props = StackScreenProps<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.OrderManagement
>;

type NavigationType = StackNavigationProp<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.DraftDetails
>;

const ROW_HEIGHT = 62;
const SECTION_HEADER_HEIGHT = 45;

export const OrderManagementScreen: React.FC<Props> = () => {
  const [styles, theme] = useStyles();
  const { navigate, goBack } = useNavigation<NavigationType>();
  const nativeGesture = Gesture.Native();
  const {
    refresh: refreshCurrentOrders,
    refreshing: refreshingCurrentOrders,
    sectionedCurrentOrders,
    loading: isCurrentOrdersLoading,
  } = useCurrentOrders(partnerSupplier.businessOrganizationUUID);
  const { employeeCan } = useAuthenticatedUserContext();
  const { filter, pillButtonProps } = useCallSheetFiltering();
  const {
    hasMore,
    loading: isPastOrdersLoading,
    loadMore,
    fetchMore,
    refresh: refreshPastOrders,
    refreshing: refreshingPastOrders,
    sectionedPastOrders,
  } = usePastOrders({
    businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
  });
  const supplierUUID = useMemo(() => {
    const firstCurrentOrder = sectionedCurrentOrders.all.find(
      (section) => section.data.length > 0,
    )?.data[0]?.metadata?.order;
    const firstPastOrder = sectionedPastOrders.find(
      (section) => section.data.length > 0,
    )?.data[0]?.metadata?.order;

    return firstCurrentOrder?.supplier_id || firstPastOrder?.supplier_id;
  }, [sectionedCurrentOrders.all, sectionedPastOrders]);

  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  const [operatorContacts, setOperatorContacts] = useState<
    OperatorEmployeeContact[] | null
  >(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isScrollDragging, setIsScrollDragging] = useState(false);

  const filteredCurrentOrders = useCallback(
    (filter: FilterType) => {
      switch (filter) {
        case FilterType.ALL:
          return sectionedCurrentOrders.all;
        case FilterType.CALL_SHEET:
          return sectionedCurrentOrders.scheduled;
        case FilterType.UNSCHEDULED:
          return sectionedCurrentOrders.unscheduled;
        default:
          return sectionedCurrentOrders.all;
      }
    },
    [sectionedCurrentOrders],
  );

  useEffect(() => {
    fennelTrack(AnalyticEvent.ORDER_MANAGEMENT_VIEW, {});
  }, []);

  const onNavigateToDraftDetails = useCallback(
    (orderDetails: OrderDetails) => {
      navigate(INSIGHTS_HUB_ROUTES.DraftDetails, orderDetails);
      fennelTrack(AnalyticEvent.ORDER_PRESS, {
        ...orderDetails,
        isDraft: true,
        origin: INSIGHTS_HUB_ROUTES.OrderManagement,
      });
    },
    [navigate],
  );

  const onNavigateToAccountDetails = useCallback(
    (order: HubOrder) => {
      if (isCurrentOrder(order) && order.is_call_sheet_order) {
        navigate(APP_MODAL_ROUTES.LocationManager, {
          params: {
            restaurantName: getTranslationValue(order.restaurant_name_i18n),
            restaurantUUID: order.restaurant_id,
            supplierUUID: order.supplier_id,
          },
          screen: LOCATION_MANAGER_ROUTES.ViewAccount,
        });
      }
    },
    [navigate],
  );

  const onNavigateToPlacedOrderDetails = useCallback(
    (orderDetails: OrderDetails) => {
      navigate(INSIGHTS_HUB_ROUTES.OrderDetails, orderDetails);
      fennelTrack(AnalyticEvent.ORDER_PRESS, {
        ...orderDetails,
        isDraft: false,
        origin: INSIGHTS_HUB_ROUTES.OrderManagement,
      });
    },
    [navigate],
  );

  const onNavigateToInvoiceDetails = useCallback(
    (invoiceDetails: InvoiceDetails) => {
      navigate(INSIGHTS_HUB_ROUTES.InvoiceDetails, invoiceDetails);
      fennelTrack(AnalyticEvent.INVOICE_PRESS, {
        ...invoiceDetails,
        origin: INSIGHTS_HUB_ROUTES.OrderManagement,
      });
    },
    [navigate],
  );

  const onNavigateToOrderDetails = useCallback(
    (order: GetHubCurrentOrdersResponse) => {
      if (Platform.OS === 'web') {
        // TODO(DSR-2973): navigation to order management disabled on web for now
        return;
      }

      // Invoice-only data can be included, which won't have a navigable order uuid
      if (!order.order_id) {
        return;
      }

      const orderDetails: OrderDetails = {
        contacts: order.operator_employees,
        orderUUID: order.order_id,
        restaurantUUID: order.restaurant_id,
        supplierUUID: order.supplier_id,
      };

      if (order.is_draft_order) {
        onNavigateToDraftDetails(orderDetails);
      } else {
        onNavigateToPlacedOrderDetails(orderDetails);
      }
    },
    [onNavigateToDraftDetails, onNavigateToPlacedOrderDetails],
  );

  const navigationState = useMemo(
    () => ({
      index: tabIndex,
      routes: [
        { key: CurrentOrderTabRoutes.Current, title: i18n.t('current') },
        { key: CurrentOrderTabRoutes.Past, title: i18n.t('past') },
      ],
    }),
    [tabIndex],
  );

  const orderAmountMicros = useCallback((order: HubOrder) => {
    return isCurrentOrder(order)
      ? order.order_amount_in_micros
      : order.amount_in_micros;
  }, []);

  const onPressOrder = useCallback(
    (order: HubOrder) => {
      if (
        isCurrentOrder(order) &&
        order.is_draft_order === null &&
        order.is_call_sheet_order
      ) {
        onNavigateToAccountDetails(order);
        return;
      }

      if (isCurrentOrder(order)) {
        onNavigateToOrderDetails(order);
        return;
      }

      // navigate to invoice details if available
      if (isPastOrder(order) && order.invoice_id) {
        onNavigateToInvoiceDetails({
          invoiceUUID: order.invoice_id,
          restaurantUUID: order.restaurant_id,
          supplierUUID: order.supplier_id,
        });
        return;
      }

      // Navigate to placed order details if available
      if (isPastOrder(order) && order.order_id) {
        onNavigateToPlacedOrderDetails({
          contacts: order.operator_employees || [],
          orderUUID: order.order_id,
          restaurantUUID: order.restaurant_id,
          supplierUUID: order.supplier_id,
        });
        return;
      }
    },
    [
      onNavigateToAccountDetails,
      onNavigateToInvoiceDetails,
      onNavigateToOrderDetails,
      onNavigateToPlacedOrderDetails,
    ],
  );

  const renderSectionHeader: (
    item: ListSectionHeader<{ title: string }>,
  ) => React.ReactElement | null = useCallback(
    (item) => {
      if (!item.metadata) {
        return null;
      }

      return (
        <View style={styles.headerContainer}>
          <Typography variant="heading-md">{item.metadata.title}</Typography>
        </View>
      );
    },
    [styles],
  );

  const renderItem: ListRenderItem<HubOrderOption> = useCallback(
    (info) => {
      const order = info.item.metadata!.order;
      const orderAmount = orderAmountMicros(order);
      const orderAmountFormatted =
        orderAmount !== null && orderAmount !== undefined
          ? formatCurrency(orderAmount, supplierCurrencyCode)
          : undefined;
      const itemCountDisplay = order.item_count_display
        ? getTranslationValue(order.item_count_display)
        : undefined;

      return (
        <RestaurantInfoHeader
          onPress={() => onPressOrder(order)}
          restaurantUUID={order.restaurant_id}
          rightElement={
            <OrderAction
              contacts={
                isCurrentOrder(order)
                  ? order.is_draft_order
                    ? order.operator_employees
                    : undefined
                  : undefined
              }
              itemCountDisplay={itemCountDisplay}
              onPress={() =>
                isCurrentOrder(order)
                  ? setOperatorContacts(order.operator_employees)
                  : null
              }
              orderAmount={orderAmountFormatted}
            />
          }
          style={styles.orderListContainer}
          subtitle={
            isCurrentOrder(order) ? order.description : order.order_date_display
          }
          title={order.restaurant_name_i18n}
        />
      );
    },
    [onPressOrder, orderAmountMicros, styles, supplierCurrencyCode],
  );

  const renderTabBarLabel: TabViewProps<TabRoute>['label'] = useCallback(
    ({ color, route }: { color: string; route: TabRoute }) => {
      return (
        <Typography
          color={color ?? theme.sk.color.text.primary}
          testID={route.title}
          variant="label-lg"
        >
          {route.title}
        </Typography>
      );
    },
    [theme],
  );

  const renderPillButton = useCallback((button: PillButtonType) => {
    return <PillButton button={button} key={button.label} />;
  }, []);

  const isRefreshing = refreshingCurrentOrders || refreshingPastOrders;

  const { isEnabled, showDots } = usePullToRefreshState({
    isRefreshing,
    isScrollDragging,
  });

  const renderScene: TabViewProps<TabRoute>['renderScene'] = useCallback(
    ({ route }) => {
      const isCurrentTab = route.key === CurrentOrderTabRoutes.Current;

      const getEmptyStateProps = () => {
        if (!isCurrentTab || filter === FilterType.ALL) {
          return {
            description: i18n.t('current_orders.no_orders.description'),
            icon: ViewCircleIcon,
            title: i18n.t('current_orders.no_orders.title'),
          };
        } else if (filter === FilterType.CALL_SHEET) {
          return {
            description: i18n.t(
              'current_orders.filter.call_sheet.empty_state.description',
            ),
            icon: EmptySheetIcon,
            title: i18n.t('current_orders.filter.call_sheet.empty_state.title'),
          };
        } else if (filter === FilterType.UNSCHEDULED) {
          return {
            description: i18n.t(
              'current_orders.filter.unscheduled.empty_state.description',
            ),
            icon: EmptyClockIcon,
            title: i18n.t(
              'current_orders.filter.unscheduled.empty_state.title',
            ),
          };
        }
      };

      const EmptyStateByFilter = () => {
        // suppress empty state until past orders are done loading
        if (!isCurrentTab && isPastOrdersLoading) {
          return null;
        }

        const emptyStateProps = getEmptyStateProps();
        const Icon = emptyStateProps?.icon;

        return emptyStateProps ? (
          <EmptyState
            description={emptyStateProps.description}
            icon={<Icon stroke={theme.sk.color.icon.secondary} />}
            style={{
              minHeight: Dimensions.get('window').height * 0.6,
            }}
            title={emptyStateProps.title}
          />
        ) : null;
      };

      const pullToRefreshComponent = (
        <PullToRefreshLoader
          height={Math.max(-offset, 0)}
          isEnabled={isEnabled}
          isScrollDragging={isScrollDragging}
          progress={Math.min(
            (Math.max(-offset, 0) / DEFAULT_MAX_PROGRESS_OFFSET) * 100,
            100,
          )}
          showDots={showDots}
        />
      );

      const sharedProps = {
        estimatedItemSize: ROW_HEIGHT + LIST_ITEM_MARGIN,
        keyExtractor: (item: HubOrderOption) => String(item.value),
        ListEmptyComponent: <EmptyStateByFilter />,
        listItemSeparatorProps: { style: styles.listItemSeparator },
        onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => {
          setOffset(e.nativeEvent?.contentOffset?.y ?? 0);
        },
        onScrollBeginDrag: () => {
          setIsScrollDragging(true);
        },
        onScrollEndDrag: () => {
          setIsScrollDragging(false);
          setOffset(0);
        },
        renderItem,
        renderListHeaderAsListItem: false,
        renderSectionHeader,
        sectionHeaderHeight: SECTION_HEADER_HEIGHT,
        showsVerticalScrollIndicator: false,
      };

      switch (route.key) {
        case CurrentOrderTabRoutes.Current: {
          const CurrentOrdersFilterPills = employeeCan(
            PermissionAction.Read,
            PermissionSubject.CallSheet,
          ) ? (
            <FilterPills
              buttons={pillButtonProps}
              containerStyle={styles.pillContainer}
              disableClear
              loading={isCurrentOrdersLoading}
              renderPillButton={renderPillButton}
            />
          ) : null;

          if (
            isCurrentOrdersLoading &&
            sectionedCurrentOrders.all.length === 0
          ) {
            return (
              <>
                {CurrentOrdersFilterPills}
                <View style={styles.loadingContainer}>
                  <CurrentOrdersListSkeleton />
                </View>
              </>
            );
          }

          return (
            <>
              {pullToRefreshComponent}
              <RecyclerList<HubOrderOption, { title: string }>
                {...sharedProps}
                accessibilityLabel={i18n.t('current')}
                ListHeaderComponent={CurrentOrdersFilterPills}
                refreshControl={
                  <RefreshControl
                    colors={['transparent']}
                    onRefresh={async () => {
                      await refreshCurrentOrders(true);
                    }}
                    // all properties must be transparent to hide the original loader
                    refreshing={refreshingCurrentOrders}
                    style={styles.nativeRefreshControl}
                    tintColor="transparent"
                  />
                }
                sections={filteredCurrentOrders(filter)}
              />
            </>
          );
        }
        case CurrentOrderTabRoutes.Past: {
          if (isPastOrdersLoading && sectionedPastOrders.length === 0) {
            return (
              <View style={styles.loadingContainer}>
                <CurrentOrdersListSkeleton />
              </View>
            );
          }
          return (
            <>
              {pullToRefreshComponent}
              <RecyclerList<HubOrderOption, { title: string }>
                {...sharedProps}
                accessibilityLabel={i18n.t('past')}
                ListFooterComponent={
                  fetchMore && hasMore ? (
                    <>
                      <ListItemSeparator style={styles.listItemSeparator} />
                      <ListItemSkeleton />
                    </>
                  ) : null
                }
                onEndReached={hasMore ? loadMore : undefined}
                refreshControl={
                  <RefreshControl
                    colors={['transparent']}
                    onRefresh={async () => {
                      await refreshPastOrders(true);
                    }}
                    // all properties must be transparent to hide the original loader
                    refreshing={refreshingPastOrders}
                    style={styles.nativeRefreshControl}
                    tintColor="transparent"
                  />
                }
                sections={sectionedPastOrders}
              />
            </>
          );
        }
        default: {
          return null;
        }
      }
    },
    [
      styles.listItemSeparator,
      styles.pillContainer,
      styles.nativeRefreshControl,
      styles.loadingContainer,
      renderItem,
      renderSectionHeader,
      filter,
      isPastOrdersLoading,
      theme.sk.color.icon.secondary,
      employeeCan,
      pillButtonProps,
      isCurrentOrdersLoading,
      renderPillButton,
      sectionedCurrentOrders.all.length,
      isEnabled,
      isScrollDragging,
      offset,
      showDots,
      refreshingCurrentOrders,
      filteredCurrentOrders,
      refreshCurrentOrders,
      sectionedPastOrders,
      fetchMore,
      hasMore,
      loadMore,
      refreshingPastOrders,
      refreshPastOrders,
    ],
  );

  return (
    <PageContainer
      containerStyle={styles.container}
      navigationHeaderProps={{
        BackButtonIcon: () => (
          <TouchableOpacity accessibilityRole="button" onPress={goBack}>
            <ArrowLeftThinIcon
              fill={theme.sk.color.icon.primary}
              height={24}
              width={24}
            />
          </TouchableOpacity>
        ),
        centerTitle: true,
        showBackButton: true,
        showShadow: false,
        title: <Typography variant="heading-sm">{i18n.t('orders')}</Typography>,
      }}
    >
      {/* Workaround to allow native back swipe gesture to work on iOS
          by wrapping TabView in a View container
          https://github.com/react-navigation/react-navigation/issues/11245#issuecomment-1991499288 */}
      <View style={styles.gestureContainer}>
        <TabView<TabRoute>
          indicatorStyle={styles.tabIndicator}
          label={renderTabBarLabel}
          lazy
          navigationState={navigationState}
          onIndexChange={(newIndex) => {
            setTabIndex(newIndex);
            fennelTrack(AnalyticEvent.ORDER_MANAGEMENT_TAB, {
              tabName: navigationState.routes[newIndex].title,
            });
          }}
          renderScene={renderScene}
          style={styles.tabBar}
          tabBarTabStyle={styles.tabBarTab}
        />
        {Platform.OS !== 'web' ? (
          <GestureDetector gesture={nativeGesture}>
            <View style={styles.edgeSwipeZone} />
          </GestureDetector>
        ) : null}
      </View>
      {operatorContacts && operatorContacts.length > 0 ? (
        <PhonePickerBottomSheet
          contacts={operatorContacts}
          onDismiss={() => setOperatorContacts(null)}
        />
      ) : null}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  edgeSwipeZone: {
    inset: 0,
    position: 'absolute',
    width: GESTURE_RESPONSE_DISTANCE,
  },
  gestureContainer: {
    flex: 1,
  },
  headerContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    marginHorizontal: LIST_ITEM_MARGIN,
    paddingBottom: 4,
    paddingTop: LIST_ITEM_MARGIN,
  },
  listItemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: LIST_ITEM_MARGIN,
  },
  loadingContainer: {
    marginHorizontal: LIST_ITEM_MARGIN,
  },
  nativeRefreshControl: {
    backgroundColor: 'transparent',
  },
  orderListContainer: {
    marginVertical: LIST_ITEM_MARGIN,
    paddingHorizontal: 16,
  },
  pillContainer: {
    marginTop: 8,
  },
  // TODO(DSR-2987): look into adding a divider to TabView
  tabBar: {
    ...shadow({
      color: theme.sk.color.border.primary,
      opacity: 100,
      radius: 4,
      x: 0,
      y: 0,
    }),
  },
  tabBarTab: {
    paddingTop: 4,
  },
  tabIndicator: {
    backgroundColor: theme.sk.color.bg.button.primary.default,
    height: 2,
  },
}));
